import React, { useMemo, useRef, useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import fCss from './fCss.module.scss';

function validateEmail(v = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export const ContactUs = () => {
  const [form, setForm] = useState({
    user_name: '',
    user_email: '',
    message: '',
  });

  const formRef = useRef(null);

  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // (valgfritt) init én gang så du slipper å sende publicKey hver gang
  useEffect(() => {
    emailjs.init({ publicKey: 'FgLxzEO_1IGLXfUD-' }); // dobbeltsjekk at denne er 100% identisk i EmailJS
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!form.user_name.trim()) e.user_name = 'Vennligst skriv ditt navn.';
    if (!form.user_email.trim()) e.user_email = 'Vennligst skriv din e-post.';
    else if (!validateEmail(form.user_email)) e.user_email = 'Vennligst skriv en gyldig e-postadresse.';
    if (!form.message.trim()) e.message = 'Vennligst skriv en melding.';
    else if (form.message.trim().length < 10) e.message = 'Litt mer detaljer må til (min 10 tegn).';
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setTouched({ user_name: true, user_email: true, message: true });

    if (!isValid) return;
    setSubmitting(true);

    try {
      await emailjs.sendForm(
        'service_orodo6s',
        'template_cjax8vm',
        formRef.current
        // publicKey kan droppes her siden vi kjørte emailjs.init()
      );
      setDone(true);
      // Rydd opp i state OG DOM etter vellykket send
      setForm({ user_name: '', user_email: '', message: '' });
      setTouched({});
      formRef.current?.reset();
    } catch (err) {
      console.error('EmailJS FAILED', err);
      setSubmitError('Something went wrong while sending. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={fCss.container} role="status" aria-live="polite">
        <div className={fCss.card}>
          <div className={fCss.successIcon} aria-hidden="true">✓</div>
          <h3 className={fCss.title}>Melding sendt!</h3>
          <p className={fCss.sub}>
            Takk, {form.user_name.split(' ')[0] || 'friend'}. Jeg svarer deg ila 24 timer.
          </p>
          <button
            className={fCss.btnGhost}
            onClick={() => {
              setDone(false);
              setForm({ user_name: '', user_email: '', message: '' });
              setTouched({});
              setSubmitError('');
            }}
          >
            Send en til
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={fCss.container}>
      <form ref={formRef} className={fCss.card} onSubmit={handleSubmit} noValidate>
        <h2 className={fCss.title}><strong>KONTAKT</strong></h2>
        <p className={fCss.sub}>Kort og konsist er perfekt - jeg leser alle meldinger og svarer ila 24t.</p>

        {/* Name */}
        <div className={fCss.field}>
          <label htmlFor="user_name" className={fCss.label}>Navn</label>
          <input
            id="user_name"
            name="user_name"
            type="text"
            className={fCss.input}
            placeholder="Ditt navn"
            value={form.user_name}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={touched.user_name && !!errors.user_name}
            aria-describedby={touched.user_name && errors.user_name ? 'err-user_name' : undefined}
            autoComplete="name"
          />
          {touched.user_name && errors.user_name && (
            <div id="err-user_name" className={fCss.error}>{errors.user_name}</div>
          )}
        </div>

        {/* Email */}
        <div className={fCss.field}>
          <label htmlFor="user_email" className={fCss.label}>Email</label>
          <input
            id="user_email"
            name="user_email"
            type="email"
            className={fCss.input}
            placeholder="you@example.com"
            value={form.user_email}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={touched.user_email && !!errors.user_email}
            aria-describedby={touched.user_email && errors.user_email ? 'err-user_email' : undefined}
            autoComplete="email"
          />
          {touched.user_email && errors.user_email && (
            <div id="err-user_email" className={fCss.error}>{errors.user_email}</div>
          )}
        </div>

        {/* Message (8 lines) */}
        <div className={fCss.field}>
          <label htmlFor="message" className={fCss.label}>Melding</label>
          <textarea
            id="message"
            name="message"
            className={fCss.input}
            placeholder="Hvordan kan jeg hjelpe deg?"
            rows={8}
            value={form.message}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={touched.message && !!errors.message}
            aria-describedby={touched.message && errors.message ? 'err-message' : undefined}
          />
          {touched.message && errors.message && (
            <div id="err-message" className={fCss.error}>{errors.message}</div>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          className={fCss.button}
          disabled={!isValid || submitting}
          aria-disabled={!isValid || submitting}
        >
          {submitting ? <span className={fCss.spinner} aria-hidden="true" /> : 'Send'}
        </button>

        {/* Error banner */}
        {submitError && (
          <p role="alert" className={fCss.error} style={{ marginTop: 12 }}>
            {submitError}
          </p>
        )}

        <p className={fCss.privacy}>We respect your privacy. No spam — ever.</p>
      </form>
    </div>
  );
};



// const AGE_RANGES = [
//   '18–25',
//   '26–32',
//   '33–39',
//   '40–46',
//   '47–56',
//   '57 or older',
// ];

// const GENDERS = ['Male', 'Female', 'Gender neutral'];



// export default function MailingList({ onSubmit }) {
//   const [form, setForm] = useState({
//     name: '',
//     email: '',
//     age: '',
//     gender: '',
//   });
//   const [touched, setTouched] = useState({});
//   const [submitting, setSubmitting] = useState(false);
//   const [done, setDone] = useState(false);

//   const errors = useMemo(() => {
//     const e = {};
//     if (!form.name.trim()) e.name = 'Please enter your name.';
//     if (!form.email.trim()) e.email = 'Please enter your email.';
//     else if (!validateEmail(form.email)) e.email = 'Please enter a valid email.';
//     if (!form.age) e.age = 'Please select an age range.';
//     if (!form.gender) e.gender = 'Please select a gender option.';
//     return e;
//   }, [form]);

//   const isValid = Object.keys(errors).length === 0;

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setForm((f) => ({ ...f, [name]: value }));
//   };

//   const handleBlur = (e) => {
//     const { name } = e.target;
//     setTouched((t) => ({ ...t, [name]: true }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setTouched({ name: true, email: true, age: true, gender: true });

//     if (!isValid) return;
//     setSubmitting(true);

//     try {
//       // Plug your API here:
//       // await fetch('/api/subscribe', { method:'POST', body: JSON.stringify(form) })
//       if (onSubmit) {
//         await onSubmit(form);
//       } else {
//         // Simulate latency
//         await new Promise((r) => setTimeout(r, 800));
//       }
//       setDone(true);
//     } catch {
//       // Optional: show toast or set an error banner
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   if (done) {
//     return (
//       <div className={styles.container} role="status" aria-live="polite">
//         <div className={styles.card}>
//           <div className={styles.successIcon} aria-hidden="true">✓</div>
//           <h3 className={styles.title}>You’re on the list!</h3>
//           <p className={styles.sub}>
//             Thanks, {form.name.split(' ')[0] || 'friend'}. We’ll keep you posted.
//           </p>
//           <button
//             className={styles.btnGhost}
//             onClick={() => { setDone(false); setForm({ name:'', email:'', age:'', gender:'' }); setTouched({}); }}
//           >
//             Add another
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className={styles.container}>
//       <form className={styles.card} onSubmit={handleSubmit} noValidate>
//         <h2 className={styles.title}><strong>MAILING LIST</strong></h2>
//         <p className={styles.sub}>Short and sweet updates. No spam.</p>

//         {/* Name */}
//         <div className={styles.field}>
//           <label htmlFor="name" className={styles.label}>Name</label>
//           <input
//             id="name"
//             name="name"
//             type="text"
//             className={styles.input}
//             placeholder="Your name"
//             value={form.name}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             aria-invalid={touched.name && !!errors.name}
//             aria-describedby={touched.name && errors.name ? 'err-name' : undefined}
//             autoComplete="name"
//           />
//           {touched.name && errors.name && (
//             <div id="err-name" className={styles.error}>{errors.name}</div>
//           )}
//         </div>

//         {/* Email */}
//         <div className={styles.field}>
//           <label htmlFor="email" className={styles.label}>Email</label>
//           <input
//             id="email"
//             name="email"
//             type="email"
//             className={styles.input}
//             placeholder="you@example.com"
//             value={form.email}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             aria-invalid={touched.email && !!errors.email}
//             aria-describedby={touched.email && errors.email ? 'err-email' : undefined}
//             autoComplete="email"
//           />
//           {touched.email && errors.email && (
//             <div id="err-email" className={styles.error}>{errors.email}</div>
//           )}
//         </div>

//         {/* Age */}
//         <div className={styles.field}>
//           <label htmlFor="age" className={styles.label}>Age</label>
//           <select
//             id="age"
//             name="age"
//             className={styles.select}
//             value={form.age}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             aria-invalid={touched.age && !!errors.age}
//             aria-describedby={touched.age && errors.age ? 'err-age' : undefined}
//           >
//             <option value="" disabled>Select your range</option>
//             {AGE_RANGES.map((r) => (
//               <option key={r} value={r}>{r}</option>
//             ))}
//           </select>
//           {touched.age && errors.age && (
//             <div id="err-age" className={styles.error}>{errors.age}</div>
//           )}
//         </div>

//         {/* Gender */}
//         <div className={styles.field}>
//           <label htmlFor="gender" className={styles.label}>Gender</label>
//           <select
//             id="gender"
//             name="gender"
//             className={styles.select}
//             value={form.gender}
//             onChange={handleChange}
//             onBlur={handleBlur}
//             aria-invalid={touched.gender && !!errors.gender}
//             aria-describedby={touched.gender && errors.gender ? 'err-gender' : undefined}
//           >
//             <option value="" disabled>Select an option</option>
//             {GENDERS.map((g) => (
//               <option key={g} value={g}>{g}</option>
//             ))}
//           </select>
//           {touched.gender && errors.gender && (
//             <div id="err-gender" className={styles.error}>{errors.gender}</div>
//           )}
//         </div>

//         <button
//           type="submit"
//           className={styles.button}
//           disabled={!isValid || submitting}
//         >
//           {submitting ? <span className={styles.spinner} aria-hidden="true" /> : 'Subscribe'}
//         </button>

//         {/* privacy hint (optional) */}
//         <p className={styles.privacy}>We respect your privacy. Unsubscribe anytime.</p>
//       </form>
//     </div>
//   );
// }